import { ApplicantDetails, Application, IApplicantOffer } from '../../apply';
import { CamelToSnake, RenameKey } from '../../mapped-types';
import { ApiFilterKeys } from '../../api';

export enum EApplicantOfferPGKeys {
  COURSE_ID = 'course_id',
  COURSE_NAME = 'course_name',
  MODE = 'course_mode',
  ENTRY_POINT = 'course_entry_point',
  KNOWN_BY = 'known_by',
  GIVEN_NAME = 'given_name',
  FAMILY_NAME = 'family_name',
  EMAIL = 'email',
  NATIONALITY = 'nationality',
  POSTCODE = 'postcode',
  CITY = 'city',
  BIRTH_COUNTRY = 'birth_country',
  RESIDENCE_COUNTRY = 'residence_country',
  APPLICATION_COUNTRY = 'application_country',
  OFFER_ID = 'offer_id',
  APPLICATION_ID = 'application_id',
  FEE_STATUS = 'fee_status',
  STATUS = 'status',
  INTAKE_ID = 'intake_id',
  UNI = 'uni',
  USER_ID = 'user_id',
  APPLIED_DATE = 'applied_date',
  ISSUE_DATE = 'issue_date',
  CONDITION_TYPE = 'condition_type',
  APPLICANT_STATUS = 'applicant_status',
  PENDING_CHANGES = 'pending_changes',
  REQUIREMENTS_IN_ASSESSMENT = 'requirements_in_assessment',
  REQUIREMENTS_WITH_CONDITION = 'requirements_with_condition',
  APPLICANT_LAST_UPDATED_AT = 'applicant_last_updated_at',
  STAGE_CHANGE_DATE = 'stage_change_date',
  NEW_DOCUMENTS_COUNT = 'new_documents_count',
  AGENT_ID = 'agent_id',
  TAGS = 'tags'
}

// PG Record of "ApplicantOffer" draws information from ApplicantDetails, Application, and IApplicantOffer
export type IApplicantOfferPGRecord = {
  p_key: string;
  pending_changes: boolean;
  requirements_in_assessment: number;
  requirements_with_condition: number;
  applicant_last_updated_at: string;
  stage_change_date: string;
  new_documents_count: number;
} & RenameKey<
  Pick<
    CamelToSnake<ApplicantDetails> &
      CamelToSnake<Application> &
      CamelToSnake<IApplicantOffer['programme']> &
      CamelToSnake<ApplicantDetails['primaryResidence']> &
      CamelToSnake<Application['residentialAddress']> &
      Omit<CamelToSnake<IApplicantOffer>, 'programme' | 'requirements'>,
    | EApplicantOfferPGKeys.COURSE_ID
    | EApplicantOfferPGKeys.COURSE_NAME
    | 'mode'
    | 'entry_point'
    | EApplicantOfferPGKeys.KNOWN_BY
    | EApplicantOfferPGKeys.GIVEN_NAME
    | EApplicantOfferPGKeys.FAMILY_NAME
    | EApplicantOfferPGKeys.EMAIL
    | EApplicantOfferPGKeys.NATIONALITY
    | EApplicantOfferPGKeys.POSTCODE
    | EApplicantOfferPGKeys.CITY
    | 'country_of_birth' // countryOfBirth
    | 'applying_from' // applyingFrom
    | 'country_of_residence' // primaryResidence -> countryOfResidence
    | 'id'
    | EApplicantOfferPGKeys.APPLICATION_ID
    | EApplicantOfferPGKeys.FEE_STATUS
    | EApplicantOfferPGKeys.STATUS
    | EApplicantOfferPGKeys.INTAKE_ID
    | 'university_name'
    | EApplicantOfferPGKeys.USER_ID
    | EApplicantOfferPGKeys.APPLIED_DATE
    | EApplicantOfferPGKeys.ISSUE_DATE
    | EApplicantOfferPGKeys.CONDITION_TYPE
    | EApplicantOfferPGKeys.APPLICANT_STATUS
    | EApplicantOfferPGKeys.AGENT_ID
    | EApplicantOfferPGKeys.TAGS
  >,
  {
    university_name: EApplicantOfferPGKeys.UNI;
    id: EApplicantOfferPGKeys.OFFER_ID;
    mode: EApplicantOfferPGKeys.MODE;
    entry_point: EApplicantOfferPGKeys.ENTRY_POINT;
    applied_date: EApplicantOfferPGKeys.APPLIED_DATE;
    issue_date: EApplicantOfferPGKeys.ISSUE_DATE;
    condition_type: EApplicantOfferPGKeys.CONDITION_TYPE;
    applicant_status: EApplicantOfferPGKeys.APPLICANT_STATUS;

    applying_from: EApplicationPGKeys.APPLICATION_COUNTRY;
    country_of_birth: EApplicantOfferPGKeys.BIRTH_COUNTRY;
    country_of_residence: EApplicantOfferPGKeys.RESIDENCE_COUNTRY;

    pending_changes: 'pending_changes';
    requirements_in_assessment: 'requirements_in_assessment';
    requirements_with_condition: 'requirements_with_condition';
    applicant_last_updated_at: 'applicant_last_updated_at';
    stage_change_date: 'stage_change_date';
  }
>;

export const IApplicantOfferPGTypeMap: Record<
  keyof IApplicantOfferPGRecord,
  Array<'string' | 'string[]' | 'integer' | 'float' | 'boolean' | 'jsonb' | 'date' | 'required'>
> = {
  p_key: ['string', 'required'],
  [EApplicantOfferPGKeys.UNI]: ['string', 'required'],
  [EApplicantOfferPGKeys.USER_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.OFFER_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.INTAKE_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.APPLICATION_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.STATUS]: ['string', 'required'],
  [EApplicantOfferPGKeys.EMAIL]: ['string', 'required'],
  [EApplicantOfferPGKeys.KNOWN_BY]: ['string', 'required'],
  [EApplicantOfferPGKeys.GIVEN_NAME]: ['string'],
  [EApplicantOfferPGKeys.FAMILY_NAME]: ['string'],
  [EApplicantOfferPGKeys.FEE_STATUS]: ['string'],
  [EApplicantOfferPGKeys.NATIONALITY]: ['string'],
  [EApplicantOfferPGKeys.POSTCODE]: ['string'],
  [EApplicantOfferPGKeys.CITY]: ['string'],
  [EApplicantOfferPGKeys.BIRTH_COUNTRY]: ['string'],
  [EApplicantOfferPGKeys.APPLICATION_COUNTRY]: ['string'],
  [EApplicantOfferPGKeys.RESIDENCE_COUNTRY]: ['string'],
  [EApplicantOfferPGKeys.COURSE_ID]: ['string'],
  [EApplicantOfferPGKeys.COURSE_NAME]: ['string'],
  [EApplicantOfferPGKeys.MODE]: ['string'],
  [EApplicantOfferPGKeys.ENTRY_POINT]: ['string'],
  [EApplicantOfferPGKeys.APPLIED_DATE]: ['string'],
  [EApplicantOfferPGKeys.ISSUE_DATE]: ['string'],
  [EApplicantOfferPGKeys.CONDITION_TYPE]: ['string'],
  [EApplicantOfferPGKeys.APPLICANT_STATUS]: ['string'],
  [EApplicantOfferPGKeys.PENDING_CHANGES]: ['boolean'],
  [EApplicantOfferPGKeys.REQUIREMENTS_IN_ASSESSMENT]: ['integer'],
  [EApplicantOfferPGKeys.REQUIREMENTS_WITH_CONDITION]: ['integer'],
  [EApplicantOfferPGKeys.APPLICANT_LAST_UPDATED_AT]: ['date'],
  [EApplicantOfferPGKeys.STAGE_CHANGE_DATE]: ['date'],
  [EApplicantOfferPGKeys.NEW_DOCUMENTS_COUNT]: ['integer'],
  [EApplicantOfferPGKeys.AGENT_ID]: ['string'],
  [EApplicantOfferPGKeys.TAGS]: ['jsonb']
};

/**
 * This object maps the relationship between backend filter keys as part of the interface IFilterKeys
 * and the PG record rows.  Not all filters are applicable to the PG record due to the summarised information available
 * on postgres.
 */
export const ApplicantOfferPGFilterMap: { [filterKey: string]: string } = Object.freeze({
  [ApiFilterKeys.NATIONALITY]: EApplicantOfferPGKeys.NATIONALITY,
  [ApiFilterKeys.BIRTH_COUNTRY]: EApplicantOfferPGKeys.BIRTH_COUNTRY,
  [ApiFilterKeys.APPLICATION_COUNTRY]: EApplicantOfferPGKeys.APPLICATION_COUNTRY,
  [ApiFilterKeys.COUNTRY]: EApplicantOfferPGKeys.RESIDENCE_COUNTRY,
  [ApiFilterKeys.FEE_STATUS]: EApplicantOfferPGKeys.FEE_STATUS,
  [ApiFilterKeys.OFFER_STATUS]: EApplicantOfferPGKeys.STATUS,
  [ApiFilterKeys.COURSE]: EApplicantOfferPGKeys.COURSE_ID
});

export enum EApplicationPGKeys {
  COURSE_ID = 'course_id',
  COURSE_NAME = 'course_name',
  KNOWN_BY = 'known_by',
  GIVEN_NAME = 'given_name',
  FAMILY_NAME = 'family_name',
  EMAIL = 'email',
  NATIONALITY = 'nationality',
  BIRTH_COUNTRY = 'birth_country',
  RESIDENCE_COUNTRY = 'residence_country',
  APPLICATION_COUNTRY = 'application_country',
  POSTCODE = 'postcode',
  CITY = 'city',
  APPLICATION_ID = 'application_id',
  FEE_STATUS = 'fee_status',
  STATUS = 'application_status',
  INTAKE_ID = 'intake_id',
  UNI = 'uni',
  USER_ID = 'user_id',
  DOB = 'dob',
  PHONE_NO = 'phone_number',
  UCAS = 'ucas_id',
  GENDER = 'gender',
  STUDY_LEVEL = 'intended_level_of_study',
  AGENT_ID = 'agent_id'
}

// PG Record of "Application" draws information from ApplicantDetails and Application
export type IApplicationPGRecord = { p_key: string } & RenameKey<
  Pick<
    CamelToSnake<Application> &
      CamelToSnake<ApplicantDetails['residentialAddress']> &
      CamelToSnake<ApplicantDetails['primaryResidence']>,
    | 'university_name' // universityName
    | 'country_of_birth' // countryOfBirth
    | 'applying_from' // applyingFrom
    | 'country_of_residence' // primaryResidence -> countryOfResidence
    | EApplicationPGKeys.USER_ID
    | EApplicationPGKeys.APPLICATION_ID
    | EApplicationPGKeys.EMAIL
    | EApplicationPGKeys.KNOWN_BY
    | EApplicationPGKeys.GIVEN_NAME
    | EApplicationPGKeys.FAMILY_NAME
    | EApplicationPGKeys.FEE_STATUS
    | EApplicationPGKeys.NATIONALITY
    | EApplicationPGKeys.DOB
    | EApplicationPGKeys.PHONE_NO
    | EApplicationPGKeys.UCAS
    | EApplicationPGKeys.GENDER
    | EApplicationPGKeys.STATUS
    | EApplicationPGKeys.INTAKE_ID
    | EApplicationPGKeys.STUDY_LEVEL
    | EApplicationPGKeys.COURSE_ID
    | EApplicationPGKeys.COURSE_NAME
    | EApplicationPGKeys.POSTCODE
    | EApplicationPGKeys.CITY
    | EApplicationPGKeys.AGENT_ID
  >,
  {
    university_name: EApplicationPGKeys.UNI;
    applying_from: EApplicationPGKeys.APPLICATION_COUNTRY;
    country_of_birth: EApplicationPGKeys.BIRTH_COUNTRY;
    country_of_residence: EApplicationPGKeys.RESIDENCE_COUNTRY;
  }
>;

export const IApplicationPGTypeMap: Record<
  keyof IApplicationPGRecord,
  Array<'string' | 'string[]' | 'integer' | 'float' | 'boolean' | 'jsonb' | 'date' | 'required'>
> = {
  p_key: ['string', 'required'],
  [EApplicationPGKeys.UNI]: ['string', 'required'],
  [EApplicationPGKeys.USER_ID]: ['string', 'required'],
  [EApplicationPGKeys.APPLICATION_ID]: ['string', 'required'],
  [EApplicationPGKeys.EMAIL]: ['string', 'required'],
  [EApplicationPGKeys.KNOWN_BY]: ['string', 'required'],
  [EApplicationPGKeys.GIVEN_NAME]: ['string'],
  [EApplicationPGKeys.FAMILY_NAME]: ['string'],
  [EApplicationPGKeys.FEE_STATUS]: ['string'],
  [EApplicationPGKeys.NATIONALITY]: ['string'],
  [EApplicationPGKeys.POSTCODE]: ['string'],
  [EApplicationPGKeys.CITY]: ['string'],
  [EApplicationPGKeys.BIRTH_COUNTRY]: ['string'],
  [EApplicationPGKeys.APPLICATION_COUNTRY]: ['string'],
  [EApplicationPGKeys.RESIDENCE_COUNTRY]: ['string'],
  [EApplicationPGKeys.DOB]: ['string'],
  [EApplicationPGKeys.PHONE_NO]: ['string'],
  [EApplicationPGKeys.UCAS]: ['string'],
  [EApplicationPGKeys.GENDER]: ['string'],
  [EApplicationPGKeys.STATUS]: ['string'],
  [EApplicationPGKeys.INTAKE_ID]: ['string'],
  [EApplicationPGKeys.STUDY_LEVEL]: ['string'],
  [EApplicationPGKeys.COURSE_ID]: ['string'],
  [EApplicationPGKeys.COURSE_NAME]: ['string'],
  [EApplicationPGKeys.AGENT_ID]: ['string']
};

export const applicationPGKeys = Object.keys(IApplicationPGTypeMap) as Array<keyof IApplicationPGRecord>;

/**
 * This object maps the relationship between backend filter keys as part of the interface IFilterKeys
 * and the PG record rows.  Not all filters are applicable to the PG record due to the summarised information available
 * on postgres.
 */
export const ApplicationPGFilterMap: { [filterKey: string]: string } = Object.freeze({
  [ApiFilterKeys.NATIONALITY]: EApplicationPGKeys.NATIONALITY,
  [ApiFilterKeys.BIRTH_COUNTRY]: EApplicationPGKeys.BIRTH_COUNTRY,
  [ApiFilterKeys.APPLICATION_COUNTRY]: EApplicationPGKeys.APPLICATION_COUNTRY,
  [ApiFilterKeys.COUNTRY]: EApplicationPGKeys.RESIDENCE_COUNTRY,
  [ApiFilterKeys.COURSE]: EApplicationPGKeys.COURSE_ID,
  [ApiFilterKeys.FEE_STATUS]: EApplicationPGKeys.FEE_STATUS,
  [ApiFilterKeys.OFFER_STATUS]: EApplicationPGKeys.STATUS
});

export type SharedPGKeys = EApplicantOfferPGKeys | EApplicationPGKeys;
